/**
 * Tiny `sessionStorage`-backed flash for the invoices list page.
 *
 * Used by the composer when it explicitly saves / deletes a draft and
 * then navigates back to `/sales/invoices`. The list page reads + clears the
 * flash on mount and surfaces an inline `Alert` for ~4 seconds.
 *
 * Why sessionStorage rather than router state / a global store: the
 * navigation is a route change (which loses React state), and pulling
 * in a toast library or a global store for one banner is overkill. The
 * key is namespaced so it never collides with other features.
 */
const STORAGE_KEY = "flint:invoice-list-flash";

export interface InvoiceListFlash {
  kind: "draft-saved" | "draft-deleted" | "invoice-issued";
  message: string;
}

export function setInvoiceListFlash(flash: InvoiceListFlash): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flash));
  } catch {
    // sessionStorage can throw in private mode / quota-exceeded; the
    // flash is best-effort UI sugar, not data, so swallow.
  }
}

export function consumeInvoiceListFlash(): InvoiceListFlash | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<InvoiceListFlash>;
    if (!parsed || typeof parsed.message !== "string" || typeof parsed.kind !== "string") {
      return null;
    }
    return { kind: parsed.kind as InvoiceListFlash["kind"], message: parsed.message };
  } catch {
    return null;
  }
}
